/**
 * KPI Card Component
 * Displays a single metric with icon and optional trend
 */

import { Link } from 'react-router-dom';
import { Icon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface KPICardProps {
  title: string;
  value: number | string;
  icon?: Icon;
  iconColor?: string;
  trend?: 'up' | 'down' | 'neutral';
  link?: string;
  subtitle?: string;
  warning?: boolean;
}

export function KPICard({
  title,
  value,
  icon: Icon,
  iconColor = 'text-gray-600',
  trend,
  link,
  subtitle,
  warning = false,
}: KPICardProps) {
  const cardContent = (
    <div
      className={cn(
        'rounded-lg border bg-white p-6 transition-all',
        link && 'cursor-pointer hover:shadow-md hover:border-gray-300',
        warning && 'border-amber-200 bg-amber-50'
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-light text-gray-600">{title}</p>
          <p
            className={cn(
              'mt-2 text-3xl font-normal',
              warning ? 'text-amber-700' : 'text-gray-900'
            )}
          >
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {subtitle && (
            <p className="mt-1 text-xs text-gray-500">{subtitle}</p>
          )}
        </div>
        {Icon && (
          <div
            className={cn(
              'rounded-full p-3',
              warning ? 'bg-amber-100' : 'bg-gray-50'
            )}
          >
            <Icon className={cn('h-5 w-5', iconColor)} />
          </div>
        )}
      </div>
      {trend && (
        <div className="mt-3 flex items-center gap-1">
          <span
            className={cn(
              'text-xs font-medium',
              trend === 'up' && 'text-green-600',
              trend === 'down' && 'text-red-600',
              trend === 'neutral' && 'text-gray-500'
            )}
          >
            {trend === 'up' && '↑'}
            {trend === 'down' && '↓'}
            {trend === 'neutral' && '→'}
          </span>
        </div>
      )}
    </div>
  );

  if (link) {
    return <Link to={link}>{cardContent}</Link>;
  }

  return cardContent;
}
